import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { DataRequestService } from '../../service/data-request.service';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { ProjectService } from 'src/app/Services/project.service';
import moment from "moment";
import { SharedService } from 'src/app/Services/sharedService';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-request-details-modal',
    templateUrl: './request-details-modal.component.html',
    styleUrl: './request-details-modal.component.css',
    standalone: false
})
export class RequestDetailsModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() requestId: number | null = null;
  @Output() closeDrawerEmitter: EventEmitter<any> = new EventEmitter<any>();

  requestDetails: any = null;
  teamList: any[] = [];
  isLoading = false;

  constructor(
    private dataRequestService: DataRequestService,
    private tokenEncryptionService: TokenEncryptionService,
    private _sharedService: SharedService,
    private projectService: ProjectService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.getTeamList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['requestId'] && this.requestId) {
      this.fetchDetails();
    } else if (!this.requestId) {
      this.requestDetails = null;
    }
  }
  
  getTeamList() {
    this.projectService.getTeamList().subscribe((res: any) => {
      this.teamList = res || [];
    });
  }

  ngOnDestroy(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { 'request-details': null },
      queryParamsHandling: 'merge'
    });
  }

  fetchDetails() {
    this.isLoading = true;
    const user = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser');
    const companyId = user?.company_id || 1;

    this.dataRequestService.getRequestById(this.requestId!, companyId).subscribe({
      next: (res) => {
        if (res.success) {
          this.requestDetails = res.data;
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to fetch details', err);
      }
    });
  }

  get isDownloadReady(): boolean {
    return !!(
      this.requestDetails &&
      this.requestDetails.action === 'Download' &&
      this.requestDetails.status === 'Completed' &&
      this.requestDetails.download_link
    );
  }

  get isDownloadLinkExpired(): boolean {
    const expiresAt = this.requestDetails?.link_expiring_at;
    if (!expiresAt) return false; // If backend didn't set it, we can't know.
    const exp = moment(expiresAt);
    if (!exp.isValid()) return false;
    return exp.isBefore(moment());
  }

  get isDownloadLinkExpiringSoon(): boolean {
    const expiresAt = this.requestDetails?.link_expiring_at;
    if (!expiresAt) return false;
    const exp = moment(expiresAt);
    if (!exp.isValid()) return false;
    return exp.isAfter(moment()) && exp.diff(moment(), 'hours', true) <= 12;
  }

  getTeamName(teamId: any): string {
    if (!teamId) return 'Unknown Team';
    const team = this.teamList.find(t => t.id == teamId);
    return team?.title?.rendered || 'Unknown Team';
  }

  closeDrawer() {
    this._sharedService.componentDrawerSubject.next({
      componentName: "data-management",
      componentData: null,
      componentAction: "close",
      componentWidth: 0
    });
  }

  formatDate(dateStr: string) {
    if (!dateStr) return '';
    return moment(dateStr).format('MMM D, YYYY');
  }

  formatTime(dateStr: string) {
    if (!dateStr) return '';
    return moment(dateStr).format('MMM D, h:mm A');
  }

  private parsePayload(payload: unknown): Record<string, unknown> | null {
    if (payload == null) return null;
    if (typeof payload === 'object') return payload as Record<string, unknown>;
    if (typeof payload === 'string') {
      try {
        return JSON.parse(payload) as Record<string, unknown>;
      } catch {
        return null;
      }
    }
    return null;
  }

  private parseRetentionScope(payload: unknown): 'logs' | 'screenshots' | null {
    const s = this.parsePayload(payload)?.scope;
    return s === 'logs' || s === 'screenshots' ? s : null;
  }

  isRollupRefreshRequest(): boolean {
    return this.requestDetails?.type === 'RollupRefresh';
  }

  rollupPayload(): { scope?: string; sliding_days?: number; backfill?: boolean } | null {
    const p = this.parsePayload(this.requestDetails?.payload);
    if (!p || p.scope !== 'pt_employee_day') return null;
    return {
      scope: String(p.scope),
      sliding_days: typeof p.sliding_days === 'number' ? p.sliding_days : undefined,
      backfill: !!p.backfill,
    };
  }

  rollupJobSummary(): {
    days_built?: number;
    rows_affected?: number;
    last_built_date?: string;
    max_log_id?: number;
  } | null {
    return this.requestDetails?.job?.logs_history?.rollup ?? null;
  }

  rollupModeLabel(): string {
    if (this.rollupPayload()?.backfill) return 'Manual backfill';
    return 'Nightly refresh';
  }

  rangeHeaderLabel(): string {
    return this.isRollupRefreshRequest() ? 'Triggered' : 'Range';
  }

  rangeDisplayValue(): string {
    const d = this.requestDetails;
    if (!d) return '';
    if (this.isRollupRefreshRequest()) {
      return this.formatDate(d.date_from);
    }
    return `${this.formatDate(d.date_from)} → ${this.formatDate(d.date_to)}`;
  }

  rollupRebuildHint(): string {
    const summary = this.rollupJobSummary();
    if (summary?.days_built != null && summary?.last_built_date) {
      const rows =
        summary.rows_affected != null
          ? ` Updated ${summary.rows_affected.toLocaleString()} rollup row(s).`
          : '';
      return `Rebuilt ${summary.days_built} calendar day(s); data through ${this.formatDate(summary.last_built_date)}.${rows}`;
    }
    const sliding = this.rollupPayload()?.sliding_days;
    if (sliding) {
      return `Rebuilds up to ${sliding} recent day(s) from activity logs. This does not delete user logs or screenshots.`;
    }
    return 'Rebuilds recent productivity rollup rows from activity logs. This does not delete user logs or screenshots.';
  }

  formatMetric(value: number | null | undefined): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return Number(value).toLocaleString();
  }

  /** Human-readable action line for the header grid (matches table chip wording). */
  actionSummaryLabel(): string {
    const d = this.requestDetails;
    if (!d) return '';
    const isSystem = (d.source || 'Admin') === 'System';

    if (d.type === 'RollupRefresh') {
      if (this.rollupPayload()?.backfill) return 'PT rollup backfill';
      return 'PT rollup refresh';
    }

    if (isSystem && d.type === 'RetentionCleanup' && d.action === 'Delete') {
      const scope = this.parseRetentionScope(d.payload);
      if (scope === 'logs') return 'Retention cleanup (Logs)';
      if (scope === 'screenshots') return 'Retention cleanup (Screenshots)';
      return 'Retention cleanup';
    }
    return d.action || '';
  }
}
